import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

export { useLocation, useParams, useSearchParams } from 'react-router';

/**
 * Imperative navigation.
 *
 * This is `useNavigate()` behind the `router.push(...)` / `router.replace(...)`
 * shape the sixty-odd call sites here already use. It exists for one reason:
 * `push` and `replace` are the *only* two methods this codebase ever called, so
 * the alternative was seventy-three near-identical edits to say the same thing.
 *
 * It is deliberately not a Next `useRouter` stand-in. There is no `refresh()`
 * (it re-fetched server components — this app has none), no `prefetch()`, and
 * no `back()`. Anything wanting more should use `useNavigate()` directly rather
 * than growing this.
 */
export function useRouter() {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
    }),
    [navigate],
  );
}

/**
 * The current path, without the query string or hash.
 *
 * `usePathname` was Next's name for it and the five call sites here all want
 * exactly this: an active-nav comparison. `useLocation().pathname` is the same
 * value; this is a name for it.
 */
export function usePathname(): string {
  return useLocation().pathname;
}

/**
 * Route params, typed as present.
 *
 * React Router types every param `string | undefined`, because a pattern may
 * declare one optional. None of ours are: `/dashboard/tools/:id/edit` cannot
 * match without an `id`, so a page the router rendered for it always has one.
 * Asserting that once, here, beats a non-null assertion on each of the twenty
 * pages that read a param.
 *
 * These pages used to take Next's `params: Promise<{ id: string }>` prop and
 * unwrap it with React's `use()`. Nothing passes props to a route element, so
 * that had to go — and it would have failed at runtime rather than in tsc,
 * because the prop's type said it was there.
 */
export function useRouteParams<T extends Record<string, string>>(): T {
  return useParams() as T;
}
